import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaHtml5, FaCode, FaArrowRight, FaGithub, FaLinkedin, FaEnvelope,
  FaJava, FaReact, FaDatabase, FaServer, FaDocker, FaGitAlt, FaNodeJs,
  FaDownload, FaRocket, FaShieldAlt, FaHospital, FaExternalLinkAlt,
  FaCss3Alt, FaMapMarkedAlt, FaHandHoldingHeart, FaPause, FaPlay,
  FaPython, FaRobot, FaChartLine, FaCloud
} from 'react-icons/fa';
import {
  SiSpringboot, SiMongodb, SiMysql, SiJavascript,
  SiTailwindcss, SiExpress, SiRedis, SiPostman, SiFlask
} from 'react-icons/si';


import './Projects.css';
import './Home.css';

/* ------------------------------------------------------------------ */
/*  DATA (component ke bahar: har render par dobara nahi banta)         */
/* ------------------------------------------------------------------ */
const roles = [
  'Full Stack Developer',
  'AI-Powered Web App Builder',
  'Java Spring Boot Developer',
  'React.js Developer',
  'Microservices Architect',
  'Backend Engineer'
];

const techStack = [
  { icon: <FaJava />, name: 'Java', color: '#ED8B00' },
  { icon: <SiSpringboot />, name: 'Spring Boot', color: '#6DB33F' },
  { icon: <FaReact />, name: 'React.js', color: '#61DAFB' },
  { icon: <FaNodeJs />, name: 'Node.js', color: '#339933' },
  { icon: <SiExpress />, name: 'Express.js', color: '#68A063' },
  { icon: <SiMysql />, name: 'MySQL', color: '#4479A1' },
  { icon: <SiMongodb />, name: 'MongoDB', color: '#47A248' },
  { icon: <SiRedis />, name: 'Redis', color: '#DC382D' },
  { icon: <SiJavascript />, name: 'JavaScript', color: '#F7DF1E' },
  { icon: <SiTailwindcss />, name: 'Tailwind', color: '#06B6D4' },
  { icon: <FaDocker />, name: 'Docker', color: '#2496ED' },
  { icon: <FaGitAlt />, name: 'Git', color: '#F05032' },
  { icon: <FaPython />, name: 'Python', color: '#3776AB' },
  { icon: <SiFlask />, name: 'Flask', color: '#E5E7EB' },
  { icon: <FaRobot />, name: 'Machine Learning', color: '#F97316' },
  { icon: <FaChartLine />, name: 'XGBoost', color: '#00D9A5' },
  { icon: <FaServer />, name: 'REST APIs', color: '#64FFDA' },
  { icon: <FaDatabase />, name: 'TiDB Cloud', color: '#DC150B' },
  { icon: <FaCloud />, name: 'Render', color: '#46E3B7' },
  { icon: <FaHtml5 />, name: 'HTML5', color: '#E34F26' },
  { icon: <FaCss3Alt />, name: 'CSS3', color: '#1572B6' },
  { icon: <SiPostman />, name: 'Postman', color: '#FF6C37' },
  { icon: <FaGithub />, name: 'GitHub', color: '#E5E7EB' },
  { icon: <FaShieldAlt />, name: 'JWT Auth', color: '#F472B6' }
];

const featuredProjects = [
  {
    id: 1,
    title: 'Bhoomi Seva',
    subtitle: 'Smart Land Records & AI Valuation',
    color: '#F97316',
    description: 'Digital land records management portal for Maharashtra. Features instant 7/12 & 8-A extract generation and an XGBoost-powered AI land valuation engine.',
    tech: ['Spring Boot', 'React', 'Python', 'TiDB MySQL'],
    metrics: { accuracy: '95% ML', records: 'Digital', speed: '<2s' },
    github: 'https://github.com/AmitIngAI/bhoomi-seva',
    live: 'https://bhoomi-frontend-f5sw.onrender.com/'
  },
  {
    id: 2,
    title: 'Majhi-Yojna',
    subtitle: 'AI Scheme Recommendation System',
    color: '#0F766E',
    description: 'AI-powered platform recommending Maharashtra government schemes to citizens using rule-based filtering and Cosine Similarity ML ranking.',
    tech: ['Spring Boot', 'React', 'Flask', 'Machine Learning'],
    metrics: { schemes: '30+', criteria: '11+ Filters', engine: 'AI/ML' },
    github: 'https://github.com/AmitIngAI/Majhi-Yojna.ai',
    live: 'https://majhi-yojna-frontend.onrender.com/'
  },
  {
    id: 3,
    title: 'MediBook Pro',
    subtitle: 'Healthcare Appointment System',
    color: '#00D9A5',
    description: 'Enterprise-grade healthcare platform with real-time appointment scheduling and secure payments.',
    tech: ['Spring Boot', 'React', 'MySQL', 'Redis'],
    metrics: { uptime: '99.9%', apis: '15+', response: '<100ms' },
    github: 'https://github.com/AmitIngAI/MediBook-Pro',
    live: 'https://medibook-pro-front.onrender.com/'
  }
];

const hideBrokenImage = (e) => {
  e.currentTarget.style.display = 'none';
};

/* ------------------------------------------------------------------ */
/*  Typing effect (alag component: sirf ye line re-render hoti hai)     */
/* ------------------------------------------------------------------ */
const TypedRole = () => {
  const [typedText, setTypedText] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = roles[roleIndex];
    let delay = isDeleting ? 50 : 100;
    if (!isDeleting && charIndex === current.length) delay = 2000; // poora likh ke ruko

    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < current.length) {
          setTypedText(current.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          setIsDeleting(true);
        }
      } else if (charIndex > 0) {
        setTypedText(current.slice(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else {
        setIsDeleting(false);
        setRoleIndex((roleIndex + 1) % roles.length);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, roleIndex]);

  return (
    <>
      I'm a <span className="typed-text">{typedText}</span>
      <span className="cursor" aria-hidden="true">|</span>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero background video                                              */
/* ------------------------------------------------------------------ */
const HeroVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p !== undefined) p.catch(() => {});
    } else {
      v.pause();
    }
  };

  // Site khulte hi video chalao (reduced-motion walon ke liye nahi)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    // pehle awaaz ke saath try karo, roka gaya to muted chalao
    v.muted = false;
    const p = v.play();
    if (p !== undefined) {
      p.catch(() => {
        v.muted = true;
        v.play().catch(() => {});
      });
    }

    // pehle click / tap / key par awaaz on karo
    function removeAll() {
      window.removeEventListener('click', unmute);
      window.removeEventListener('touchend', unmute);
      window.removeEventListener('keydown', unmute);
    }
    function unmute() {
      if (!v.ended) v.muted = false;
      removeAll();
    }
    window.addEventListener('click', unmute);
    window.addEventListener('touchend', unmute);
    window.addEventListener('keydown', unmute);

    return removeAll;
  }, []);

  return (
    <div className="hero-media">
      <video
        ref={videoRef}
        className="hero-video"
        playsInline
        preload="metadata"
        poster="/images/hero-poster.jpg"
        aria-hidden="true"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      >
        <source src="/videos/hero.mp4" type="video/mp4" />
      </video>

      <div className="video-controls">
        <button
          type="button"
          className="video-btn"
          onClick={togglePlay}
          aria-label={playing ? 'Pause background video' : 'Play background video'}
        >
          {playing ? <FaPause /> : <FaPlay />}
          <span>{playing ? 'Pause' : 'Play'}</span>
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
const Home = () => {
  const navigate = useNavigate();
  const openProject = (id) => navigate('/projects', { state: { openId: id } });

  return (
    <div className="home-page">

      {/* ===== HERO SECTION ===== */}
      <section className="hero-section">
        <HeroVideo />

        <div className="hero-container">
          <motion.div
            className="hero-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.p
              className="greeting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span className="wave" aria-hidden="true">👋</span> Hello, I am
            </motion.p>

            <motion.h1
              className="hero-name"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Amit Ingale
            </motion.h1>

            <motion.h2
              className="hero-role"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <TypedRole />
            </motion.h2>

            <motion.p
              className="hero-description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              MCA Graduate specializing in building <span className="highlight">scalable web applications</span> with
              <span className="highlight"> Java Spring Boot</span>, <span className="highlight">React.js</span>,
              Experienced in <span className="highlight"> REST APIs</span> and <span className="highlight">Database Design</span>.
              Built and deployed <span className="highlight">Bhoomi Seva</span> (land records + AI valuation),
              <span className="highlight"> Majhi-Yojna</span> (AI scheme recommender) and
              <span className="highlight"> MediBook Pro</span> (healthcare appointments).
            </motion.p>

            <motion.div
              className="hero-buttons"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <a href="/resume.pdf" download className="btn btn-secondary">
                <FaDownload /> Resume
              </a>
              <Link to="/contact" className="btn btn-outline">
                <FaEnvelope /> Connect Me
              </Link>
            </motion.div>

            <motion.div
              className="hero-social"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <span>Connect:</span>
              <div className="social-icons">
                <motion.a
                  href="https://github.com/AmitIngAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub profile"
                  whileHover={{ scale: 1.15 }}
                >
                  <FaGithub />
                </motion.a>
                <motion.a
                  href="https://linkedin.com/in/amitgingale07"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn profile"
                  whileHover={{ scale: 1.15 }}
                >
                  <FaLinkedin />
                </motion.a>
                <motion.div whileHover={{ scale: 1.15 }} style={{ display: 'inline-flex' }}>
                  <Link to="/contact" aria-label="Contact">
                    <FaEnvelope />
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== TECH STACK ===== */}
      <section className="section">
        <div className="section-header">
          <h2><FaCode aria-hidden="true" /> Tech Stack</h2>
          <p>Technologies I use to bring ideas to life</p>
        </div>

        <div className="tech-marquee">
          <div className="tech-marquee-track">
            {[...techStack, ...techStack].map((tech, index) => (
              <div
                key={`${tech.name}-${index}`}
                className="tech-item"
                aria-hidden={index >= techStack.length ? 'true' : undefined}
              >
                <span className="tech-icon" style={{ color: tech.color }}>{tech.icon}</span>
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED PROJECTS ===== */}
      <motion.section
        className="section projects-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="section-header">
          <h2><FaRocket aria-hidden="true" /> Featured Projects</h2>
          <p>Highlights from my portfolio</p>
        </div>

        <div className="hf-row">
          {featuredProjects.map((p) => (
            <div
              key={p.id}
              className="hf-card"
              style={{ '--c': p.color, '--cg': `${p.color}88` }}
              role="button"
              tabIndex={0}
              aria-label={`Open ${p.title} details`}
              onClick={() => openProject(p.id)}
              onKeyDown={(e) => {
            
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openProject(p.id);
                }
              }}
            >
              <div className="pw-float">
                <div className="pw-frame">
                  <img
                    src={`/images/proj-${p.id}.jpg`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    draggable="false"
                    onError={hideBrokenImage}
                  />
                  <div className="pw-shade" />

                  <div className="hf-info">
                    <div className="hf-head">
                      <span className="pw-badge">★ Featured</span>
                      <div className="hf-links">
                        <a
                          href={p.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`${p.title} source code on GitHub`}
                          className="hf-btn hf-gh"
                        >
                          <FaGithub /><span>Code</span>
                        </a>
                        <a
                          href={p.live}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`${p.title} live demo`}
                          className="hf-btn hf-live"
                        >
                          <FaExternalLinkAlt /><span>Live</span>
                        </a>
                      </div>
                    </div>

                    <h3>{p.title}</h3>
                    <span className="hf-sub">{p.subtitle}</span>
                    <p className="hf-desc">{p.description}</p>

                    <div className="hf-metrics">
                      {Object.entries(p.metrics).map(([k, v]) => (
                        <div key={k}><b>{v}</b><span>{k}</span></div>
                      ))}
                    </div>

                    <div className="hf-tags">
                      {p.tech.map((t) => <span key={t}>{t}</span>)}
                    </div>
                  </div>

                  <div className="pw-glass" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <motion.div className="section-action" whileHover={{ x: 10 }}>
          <Link to="/projects">
            View All Projects <FaArrowRight />
          </Link>
        </motion.div>
      </motion.section>

    </div>
  );
};

export default Home;